import { AbstractControl, ValidationErrors } from "@angular/forms";
import { Injectable } from "@angular/core";

import * as model from "../models/flow.model";
import * as dto from "../../common/models/flow.dto";
import { RuleCompiler } from "./rule-compiler.service";

@Injectable()

export class Mapper {

  constructor(public compiler: RuleCompiler) {

  }

  compileRule<T>(rule: dto.Rule, context: any, defaultValue: T): () => Promise<T> {
    return this.compiler.compileAsyncRule<T>(rule, context, defaultValue);
  }

  createInstance(f: dto.FlowInstance, context: any): model.FlowInstance {

      let result = new model.FlowInstance({
        name: f.name,
        context: context,
        id: f.id,
        templateId: f.templateId,
        templateName: f.templateName,
        organizationId: f.organizationId,
        userId: f.userId,
        isPublic: f.isPublic,
        previousState: f.previousState,
        currentState: f.currentState,
        states: [],
        currentWorkItem: f.currentWorkItem,
        associatedWorkItems: f.associatedWorkItems,
        history: f.history,
        startAtSummary: f.startAtSummary,
        onBehalf: f.onBehalf,
        isReturned: f.isReturned
    });

    result.states = (f.states || []).map(s => this.createState(result, s));
    return result;
  }

createState(flow: model.FlowInstance, s: dto.State): model.State {

    let result = new model.State({
        flow: flow,
        isInitial: s.isInitial,
        isTerminal: s.isTerminal,
        key: s.key,
        name: s.name,
        title: s.title,
        stateType: s.stateType,
    
        transitions: (s.transitions || []).map(t => new model.Transition({
            key: t.key,
            name: t.name,
            targetState: t.targetState,
            outgoingRules: t.outgoingRules.map(r => new model.ChangeTargetRule(this.compiler.compileAsyncRule(r, flow.context, null), r.contextTarget)),
            sideEffects: t.sideEffects.map(s => new model.SideEffect({
            id: s.id,
            label: s.label,
            configuration: s.configuration,
            implmentationType: s.implementationType,
            runOrder: s.runOrder
            }))
        })),
        sections: [],
    });

    result.sections = (s.sections || []).map(s => this.createSection(s, flow.context, result));
    return result;
    }

    createSection(s: dto.Section, context: any, state: model.State): model.Section {
        const result = new model.Section({
            id: s.id,
            name: s.name,
            isRequired: this.compiler.compileBooleanRule(s.isRequired, context),
            pages: []
        });
        result.pages = (s.pages || []).map(p => this.createPage(result, p, context, state));
        return result;
    }

  createPage(s: model.Section, p: dto.Page, context: any, state: model.State): model.Page {

      let result = new model.Page({
          pageType: p.pageType,
          pageNumber: p.pageNumber,
          context: context,
          state: state,
          questions: (p.questions || []).map(o => o.map(q => this.createQuestion(q, context))),
          isSectionHead: p.isSectionHead,
          section: s.id,
          sectionTitle: s.name,
          subtitle: p.subtitle,
          buttons: [],
          incomingRules:
              (p.incomingRules || [])
                  .map(r => new model.ChangeTargetRule(this.compiler.compileAsyncRule(r, context, null),
                      r.contextTarget))
      });
      const nonadhocButtons = p.buttons.filter(b => b.type !== dto.ButtonKind.AddReviewer);
   //   result.buttons = (p.buttons || []).map(b => this.createButton(s, result, b, context));
      result.buttons = (nonadhocButtons || []).map(b => this.createButton(s, result, b, context));
      result.navigationButtons = result.buttons.filter(b => b.isNavigation);
      result.specialButtons = result.buttons.filter(b => b.isSpecial);
      const partiallyRequired: dto.Question[] = [];
      for (let r = 0; r < p.questions.length; r++) {
          for (let q = 0; q < p.questions[r].length; q++) {
              for (let v = 0; v < p.questions[r][q].validationRules.length; v++) {
                  if (p.questions[r][q].validationRules[v].onSubmitOnly) { 
                      partiallyRequired.push(p.questions[r][q]);
                  }
              }
          }
      }
      result.partiallyRequiredValid = this.compiler.compilePagePartiallyRequiredRule(partiallyRequired, context);
    return result;
  }
  
  createButton(section: model.Section, page: model.Page, b: dto.Button, context: any): model.Button {

      let result = model.Button.create(b.type, {
          id: b.id,
          section: section,
          page: page,
          label: b.label,
          enabled: this.compiler.compileBooleanRule(b.enabledRule, context),
          visible: this.compiler.compileBooleanRule(b.visibleRule, context),
          notifyMessageRule: this.compiler.compileSyncRule<string>(b.notifyMessageRule, context, ""),    
          redirectToRule: this.compiler.compileSyncRule<string>(b.redirectToRule, context, ""),    
          targetTransition: b.type === dto.ButtonKind.Transition
              ? this.compiler.compileAsyncRule<string>(b.targetTransitionRule, context, "")
              : null,
          finalDestination: b.type === dto.ButtonKind.Return
              ? this.compiler.compileSyncRule<string>(b.targetTransitionRule, context, "")
              : null,
          targetPage: b.type === dto.ButtonKind.JumpToPage
              ? this.compiler.compileAsyncRule<number>(b.targetPageRule, context, -1)
              : null,
          targetSection: b.type === dto.ButtonKind.JumpToSection
              ? this.compiler.compileAsyncRule<number>(b.targetSectionRule, context, -1)
              : null,
      position: b.position,
      special: b.special,
      confirmAction: b.confirmAction,
      restrictingGroups: b.restrictingGroups,
      alwaysEnable: b.alwaysEnable
    });

    return result;
  }

  public _optionsQuestionKinds: dto.QuestionKind[] = [
    dto.QuestionKind.Combo,
    dto.QuestionKind.Dropdown,
    dto.QuestionKind.Radio,
    dto.QuestionKind.ItemList,
  ];

  createQuestion(q: dto.Question, context: any): model.Question {

      const pageRules = q.validationRules.filter(vr => !vr.onSubmitOnly);
      
      const {
          pageValidationMessages = [],
          pageValidator = () => { }
      } = this.compiler.compileValidationRules(pageRules, context);
      const {
          flowValidationMessages = [],
          flowValidator = () => { }
      } = this.compiler.compileFlowValidationRules(q, context);

      const visible = this.compiler.compileBooleanRule(q.visibleRule, context);
      const enabled = this.compiler.compileBooleanRule(q.enabledRule, context);
    let args = {
      questions: [],
      contextOptionSource: null,
      context: context,
      contextTarget: q.contextTarget,
      initialDataContextTarget: q.initialDataContextTarget,
      initialValue: this.compiler.compileSyncRule<any>(q.initialDataContextTargetRule, context, ""),
      enabled: this.generateEnabled(q, enabled, visible),
      helpText: q.helpText,
      key: q.key,
      label: q.label,
      pageValidationMessages: pageValidationMessages,
      pageValidator: pageValidator,
      flowValidationMessages: flowValidationMessages,
      flowValidator: flowValidator,
      visible: visible,
      changeRules: (q.changeRules || []).map(r => new model.ChangeTargetRule(this.compiler.compileAsyncRule(r, context, null), r.contextTarget, r.name)),
      //files: q.files,
      flexGrow: q.flexGrow,
      defaultValue: q.defaultValue,
      config: q.config
    };

    if (q.type == dto.QuestionKind.Group) {
      args.questions = q.config.questions.map(qq => this.createQuestion(qq, context))
    };

    if (this._optionsQuestionKinds.find(qq => qq === q.type)) {
      args.contextOptionSource = q.config["contextOptionSource"];
    };

    return model.Question.create(q.type, args);
    }

    generateEnabled(question: dto.Question, enabledRule: () => boolean, visibleRule: () => boolean): () => boolean {
        // FlowDataTable enabledRules were, historically, all authored backward: rule authors wrote them
        // meaning "true = should be disabled" rather than the standard "true = should be enabled" every
        // other question type uses. That convention predates this fix and is baked into however many
        // data-table enabledRules already exist across every flow - rewriting the rules themselves would
        // mean auditing and touching an unknown number of them, which is a much bigger risk than
        // correcting the interpretation once, here, at the one place both Mapper and
        // OnBehalfMapperService.createQuestion() funnel through. Only invert when an enabledRule is
        // actually present - a data table with no rule at all should still default to enabled, same as
        // every other question type, since there's no backward-authored rule to compensate for.
        const invertForDataTable = question.type === dto.QuestionKind.FlowDataTable && question.enabledRule != null;
        return () => {
            const effectiveEnabledResult = invertForDataTable ? !enabledRule() : enabledRule();
            if (question.type === dto.QuestionKind.DataQueryButton && question.config.runOnInit) {
                return effectiveEnabledResult;
            }
            return effectiveEnabledResult && visibleRule();
        };
    }
}

